import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Activity, CheckCircle, ExternalLink, ShieldAlert } from 'lucide-react';

import { Spinner } from '../design-system';
import { PublicBrandShell } from '../components/public/PublicBranding';
import { api, apiEndpoints } from '../utils/api';
import { PublicScanProjectResponse, PublicScanStatus } from '../types/application';

const STATUS: Record<PublicScanStatus, { label: string; className: string; icon: React.ElementType }> = {
  passing: {
    label: 'No vulnerabilities detected',
    className: 'bg-[#ADDD30]/10 text-[#C4F03E] border-[#ADDD30]/30',
    icon: CheckCircle,
  },
  failing: {
    label: 'Vulnerabilities detected',
    className: 'bg-red-500/10 text-red-300 border-red-500/25',
    icon: ShieldAlert,
  },
  unknown: {
    label: 'Scan unknown',
    className: 'bg-neutral-500/10 text-ui-text-tertiary border-ui-border-primary/25',
    icon: Activity,
  },
};

const formatDate = (value?: string | null): string => {
  if (!value) return 'Not available';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return 'Not available';
  }
};

const formatScanType = (scanType: string): string =>
  scanType.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const PublicScanProjectPage: React.FC = () => {
  const { orgSlug, projectSlug } = useParams<{ orgSlug: string; projectSlug: string }>();
  const [project, setProject] = useState<PublicScanProjectResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!orgSlug || !projectSlug) return;
      setLoading(true);
      setError(null);
      try {
        const response = await api.get<PublicScanProjectResponse>(
          apiEndpoints.publicScanProjects.get(orgSlug, projectSlug)
        );
        if (!cancelled) setProject(response);
      } catch (err: any) {
        if (!cancelled) setError(err.message || 'Public scan page not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [orgSlug, projectSlug]);

  const statusStyle = useMemo(
    () => STATUS[project?.snapshot.status || 'unknown'],
    [project?.snapshot.status]
  );
  const StatusIcon = statusStyle.icon;

  if (loading) {
    return (
      <PublicBrandShell mainClassName="flex items-center justify-center px-4 py-16">
        <Spinner size={32} />
      </PublicBrandShell>
    );
  }

  if (error || !project) {
    return (
      <PublicBrandShell mainClassName="flex items-center justify-center px-4 py-16">
        <div className="max-w-md text-center">
          <ShieldAlert className="w-12 h-12 mx-auto text-[#808080] mb-4" />
          <h1 className="text-2xl font-bold text-white">Public scan page unavailable</h1>
          <p className="mt-2 text-sm text-[#B3B3B3]">{error || 'This project is not published.'}</p>
        </div>
      </PublicBrandShell>
    );
  }

  const snapshot = project.snapshot;

  return (
    <PublicBrandShell>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <section className="border-b border-[#2A2A2A] pb-8">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm text-[#808080]">
                <span>Public security status by Kolega.dev</span>
              </div>
              <h1 className="mt-3 text-3xl sm:text-4xl font-bold text-white">{project.display_name}</h1>
              {project.description && (
                <p className="mt-3 max-w-3xl text-[#B3B3B3]">{project.description}</p>
              )}
            </div>
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold ${statusStyle.className}`}>
              <StatusIcon className="w-4 h-4" />
              {statusStyle.label}
            </div>
          </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 py-8">
          <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-4">
            <p className="text-xs text-[#808080]">Actionable Findings</p>
            <p className="mt-2 text-3xl font-bold text-white">{snapshot.actionable_findings_count}</p>
          </div>
          <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-4">
            <p className="text-xs text-[#808080]">Critical</p>
            <p className="mt-2 text-3xl font-bold text-red-300">{snapshot.findings_by_severity.critical}</p>
          </div>
          <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-4">
            <p className="text-xs text-[#808080]">High</p>
            <p className="mt-2 text-3xl font-bold text-amber-300">{snapshot.findings_by_severity.high}</p>
          </div>
          <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-4">
            <p className="text-xs text-[#808080]">Evaluated</p>
            <p className="mt-2 text-sm font-semibold text-white">{formatDate(snapshot.evaluated_at)}</p>
          </div>
        </section>

        <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-6">
          <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden">
            <div className="px-5 py-4 border-b border-[#2A2A2A]">
              <h2 className="text-base font-semibold text-white">Scan Coverage</h2>
            </div>
            <div className="divide-y divide-[#2A2A2A]">
              {snapshot.repositories.map((repo) => (
                <div key={repo.repository} className="p-5">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="font-medium truncate text-white">{repo.repository}</p>
                      <p className="text-sm text-[#808080]">{repo.branch}</p>
                    </div>
                    {repo.repository_url && (
                      <a
                        href={repo.repository_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-[#ADDD30] hover:underline"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Repository
                      </a>
                    )}
                  </div>
                  <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {repo.scans.map((scan) => (
                      <div key={scan.scan_type} className="rounded-md bg-[#0A0A0A] border border-[#2A2A2A] p-3">
                        <p className="text-sm font-medium text-white">{formatScanType(scan.scan_type)}</p>
                        <p className={scan.status === 'succeeded' && scan.fresh ? 'mt-1 text-sm text-emerald-500' : 'mt-1 text-sm text-amber-500'}>
                          {scan.status === 'succeeded' && scan.fresh ? 'Fresh' : scan.message || scan.status}
                        </p>
                        <p className="mt-2 text-xs text-[#808080]">{formatDate(scan.completed_at)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-5">
              <h2 className="text-base font-semibold text-white">Status</h2>
              <p className="mt-3 text-sm text-[#B3B3B3]">{snapshot.status_reason}</p>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-[#808080]">Freshness window</dt>
                  <dd className="font-medium text-white">{snapshot.freshness_days} days</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-[#808080]">Stale after</dt>
                  <dd className="font-medium text-right text-white">{formatDate(snapshot.stale_at)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-[#808080]">Published</dt>
                  <dd className="font-medium text-right text-white">{formatDate(project.published_at)}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg bg-[#1A1A1A] border border-[#2A2A2A] p-5">
              <h2 className="text-base font-semibold text-white">Finding Counts</h2>
              <dl className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <dt className="text-[#808080]">Open</dt>
                  <dd className="font-medium text-white">{snapshot.findings_by_status.open}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#808080]">Needs review</dt>
                  <dd className="font-medium text-white">{snapshot.findings_by_status.needs_manual_review}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#808080]">Resolved</dt>
                  <dd className="font-medium text-white">{snapshot.findings_by_status.resolved}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-[#808080]">Dismissed</dt>
                  <dd className="font-medium text-white">
                    {snapshot.findings_by_status.ignored + snapshot.findings_by_status.false_positive}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </section>
      </div>
    </PublicBrandShell>
  );
};

export default PublicScanProjectPage;
